import * as React from "react";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { Alert, Button, Input } from "components/bootstrap";
import { Spinner } from "components/common";
import { useEnableEventDefinitions } from "security-app/hooks/useEventsAPI";
import useThreatRelatedEventDefinitions from "./useThreatRelatedEventDefinitions";
const ListContainer = styled.div(
  ({ theme }) => css`
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid ${theme.colors.input.border};
    border-radius: 4px;
    padding: ${theme.spacings.sm};
  `
);
const Row = styled.label(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings.sm};
    padding: ${theme.spacings.xs} 0;
    cursor: pointer;
    font-weight: normal;
  `
);
const ActionsBar = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${theme.spacings.sm};
  `
);
const EventDefinitionsEnable = ({ coverageEntity = void 0 }) => {
  const { t } = useTranslation("enterprise_security_app");
  const { allDefinitions, notEnabledDefinitions, loadingDefinitions } = useThreatRelatedEventDefinitions(coverageEntity);
  const [selected, setSelected] = useState([]);
  const { enableEventDefinitions, enablingEventDefinitions } = useEnableEventDefinitions();
  const sortedDisabled = useMemo(
    () => [...notEnabledDefinitions].sort((a, b) => a.title.localeCompare(b.title)),
    [notEnabledDefinitions]
  );
  if (loadingDefinitions) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  if (allDefinitions.length === 0) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, t("event_definitions_enable.no_definitions"));
  }
  const toggle = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    setSelected((prev) => prev.length === sortedDisabled.length ? [] : sortedDisabled.map((d) => d.id));
  };
  const handleEnable = () => {
    enableEventDefinitions(selected).then(
      () => setSelected([]),
      () => {
      }
    );
  };
  if (sortedDisabled.length === 0) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "success" }, t("event_definitions_enable.all_enabled"));
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ActionsBar, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "select-all-event-defs",
      type: "checkbox",
      checked: selected.length === sortedDisabled.length,
      onChange: toggleAll,
      label: t("event_definitions_enable.select_all", { count: sortedDisabled.length })
    }
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "info",
      bsSize: "small",
      disabled: selected.length === 0 || enablingEventDefinitions,
      onClick: handleEnable
    },
    enablingEventDefinitions ? /* @__PURE__ */ React.createElement(Spinner, null) : t("event_definitions_enable.enable", { count: selected.length })
  )), /* @__PURE__ */ React.createElement(ListContainer, null, sortedDisabled.map((def) => /* @__PURE__ */ React.createElement(Row, { key: def.id }, /* @__PURE__ */ React.createElement(
    "input",
    {
      type: "checkbox",
      checked: selected.includes(def.id),
      onChange: () => toggle(def.id),
      "aria-label": t("event_definitions_enable.select_aria", { title: def.title })
    }
  ), /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement("b", null, def.title), def.description ? /* @__PURE__ */ React.createElement("span", null, " \u2014 ", def.description) : null)))));
};
export default EventDefinitionsEnable;
